import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from '../components/confirm-dialog/confirm-dialog.component';

export interface DialogOptions {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
}

/**
 * The app's own confirm / prompt / alert.
 *
 * The browser's are not usable: Chrome returned from window.prompt() and
 * window.confirm() without showing anything, so every action behind one did
 * nothing, silently (2026-10-01). These open a Material dialog instead and are
 * asynchronous — `if (!await this.dialogs.confirm('…')) return;`.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  constructor(private dialog: MatDialog) {}

  /** true when confirmed; false when cancelled or dismissed. */
  async confirm(message: string, options: DialogOptions = {}): Promise<boolean> {
    return (await this.open({
      title: options.title || '',
      message,
      confirmText: options.confirmText || 'OK',
      cancelText: options.cancelText,
    })) === true;
  }

  /** The text entered (trimmed, possibly empty); null when cancelled or
   *  dismissed — the same contract as window.prompt(). */
  async prompt(message: string, value: string | null = '', options: DialogOptions = {}): Promise<string | null> {
    const result = await this.open({
      title: options.title || '',
      message,
      confirmText: options.confirmText || 'OK',
      cancelText: options.cancelText,
      input: { value: value ?? '', placeholder: options.placeholder },
    });
    return typeof result === 'string' ? result : null;
  }

  /** A notice with one button. Resolves when it is closed; callers that only
   *  inform need not await it. */
  async alert(message: string, options: DialogOptions = {}): Promise<void> {
    await this.open({
      title: options.title || '',
      message,
      confirmText: options.confirmText || 'OK',
      hideCancel: true,
    });
  }

  private open(data: ConfirmDialogData): Promise<boolean | string | undefined> {
    const ref = this.dialog.open(ConfirmDialogComponent, { data, width: '440px', autoFocus: 'first-tabbable' });
    return lastValueFrom(ref.afterClosed(), { defaultValue: undefined });
  }
}
